// Offline mode feature flag.
//
// The org setting (`organization.settings.offline_mode`, or the rollout guard
// `offline_mode_v2`) arrives with the
// /auth/me profile, which is unreachable on an offline cold boot — so the
// last-known flag is persisted to localStorage (written/cleared on every
// successful login via setOfflineModeUser) and consulted as a fallback.
// The persisted copy self-heals: an org turning the setting off clears it on
// the next online session, and purgeOfflineData()/clearAllData() removes it.
//
// Two keys, either one enables: `offline_mode` is the permanent flag, and
// `offline_mode_v2` is a rollout guard. The service worker updates by prompt,
// so a client can run an older bundle for a while after a deploy, and bundles
// from before 2026-09-17 read only `offline_mode` and run the old, much
// heavier prefetch. Setting only `offline_mode_v2` turns offline mode on for
// current bundles while older ones stay inert.
//
// Remote kill: the connectivity probe reads the same setting from the public
// org row. When the org turns it off, disableOfflineModeRemotely() makes this
// module report disabled at once, without waiting for a reload.

const FLAG_KEY = 'prism_offline_mode';

export function settingsEnableOfflineMode(settings) {
  return settings?.offline_mode_v2 === true || settings?.offline_mode === true;
}

let _user = null;
let _killed = false;
const _disabledListeners = new Set();
let _persisted = readPersistedFlag();

function readPersistedFlag() {
  try {
    const raw = localStorage.getItem(FLAG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setOfflineModeUser(user) {
  _user = user;

  if (!user?.organization) return;

  try {
    if (settingsEnableOfflineMode(user.organization.settings)) {
      _killed = false;
      _persisted = { enabled: true, organizationId: user.organization.id };
      localStorage.setItem(FLAG_KEY, JSON.stringify(_persisted));
    } else {
      _persisted = null;
      localStorage.removeItem(FLAG_KEY);
    }
  } catch {
    // localStorage unavailable — in-memory flag still works for this session
  }
}

export function isOfflineModeEnabled() {
  // Dev-only escape hatch; must never enable offline mode in production
  // for an org that hasn't opted in.
  if (import.meta.env.DEV) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('offline') === 'true') {
      return true;
    }
  }

  if (_killed) return false;

  if (settingsEnableOfflineMode(_user?.organization?.settings)) {
    return true;
  }

  return Boolean(_persisted?.enabled);
}

// Subscribers run once when the org setting is found turned off remotely
// (prefetch aborts, the sync queue gets one last drain).
export function onOfflineModeDisabled(callback) {
  _disabledListeners.add(callback);
  return () => _disabledListeners.delete(callback);
}

export function disableOfflineModeRemotely() {
  if (_killed) return;
  _killed = true;
  clearPersistedOfflineFlag();
  for (const callback of _disabledListeners) {
    try {
      callback();
    } catch (e) {
      console.error('[OfflineFlag] disable listener failed:', e);
    }
  }
}

// Used by the connectivity probe before /auth/me has resolved.
export function getOfflineOrganizationId() {
  return _user?.organization?.id || _persisted?.organizationId || null;
}

// Used by offline write handlers to stamp locally created records.
export function getOfflineUserId() {
  return _user?.id || null;
}

// Called from clearAllData() so purge semantics stay "one call wipes everything".
export function clearPersistedOfflineFlag() {
  _persisted = null;
  try {
    localStorage.removeItem(FLAG_KEY);
  } catch {
    // ignore
  }
}
